import { translateTemplate } from '@/helpers/templateTranslator'

export default function ({ $sitewideData }, inject) {
    // TODO: Can we debounce this somehow?
    const generateMetas = (template = {}, route, customVariables = {}) => {
        // Construct a dictionary of variables to use in meta templates.
        const variables = {
            SITE: $sitewideData.config?.siteName,
            DOMAIN: $sitewideData.domain,
            TITLE_SUFFIX: $sitewideData.seo?.content?.titleSuffix,
            PHONE: $sitewideData.config.phoneGeneral,
            ...(customVariables || {}),
        }

        // Construct meta container and set title and description based upon templates.
        // We also set sensible defaults that almost all pages will not need to modify
        const metas = new MetaContainer()
            // For the title, we need to make sure we show a title with fitment if we have fitment and we have a specific override for that.
            .setTitle(
                translateTemplate(
                    route.params.makeSlug && template.titleFitment
                        ? template.titleFitment
                        : template.title,
                    variables
                )
            )
            .setDescription(translateTemplate(template.description, variables))
            .addMetaProperty('og:site_name', $sitewideData.config?.siteName)
            .addMetaName('twitter:card', 'summary_large_image')
            .addMetaName('twitter:label1', 'Call us')
            .addMetaName('twitter:data1', $sitewideData.config.phoneGeneral)
            .addMetaName('twitter:label2', 'Shop online')
            .addMetaName('twitter:data2', `at ${$sitewideData.domain}`)
            .setCanonical(route.path)

        // Cascade checks to get the proper description based upon fitment.
        // NOTE: we are using route params for this because we don't care about what fitment we have,
        // only that we have that specific type of fitment in the existing route
        // Fitment pages in Storyblok have their own meta description so we need to also check for that here
        if (template.descriptionFitment)
            metas.setDescription(translateTemplate(template.descriptionFitment, variables))
        else if (route.params.year && template.descriptionYear)
            metas.setDescription(translateTemplate(template.descriptionYear, variables))
        else if (route.params.modelSlug && template.descriptionModel)
            metas.setDescription(translateTemplate(template.descriptionModel, variables))
        else if (route.params.makeSlug && template.descriptionMake)
            metas.setDescription(translateTemplate(template.descriptionMake, variables))

        // NOTE: We will still need to call .format() to get actual data vue meta needs.
        return metas
    }

    inject('metaHelpers', { generateMetas })

    // Helper class to store meta information
    class MetaContainer {
        constructor() {
            this.title = null
            this.description = null
            this.canonical = null
            this.isNoIndex = false
            this.socialType = 'website'
            this.links = []
            this.metas = []
        }

        format() {
            // Set the social title the same as the meta title
            this.addMetaProperty('og:title', this.title)

            // Set the social description the same as the meta description
            this.addMetaName('description', this.description)
            this.addMetaProperty('og:description', this.description)

            // Set the social type
            this.addMetaProperty('og:type', this.socialType)

            // If we have a canonical we want the page index, add the canonical link
            if (this.canonical && !this.isNoIndex)
                this.links.push({ hid: 'canonical', rel: 'canonical', href: this.canonical })

            const options = {
                title: this.title,
                link: this.links,
                meta: this.metas,
            }

            return options
        }

        setTitle(title) {
            if (!title) return this

            // replace &amp; with & and nuke anyting else that's &XXX; to &XXXXXX;
            this.title = title.replace(/&amp;/g, '&').replace(/(?!&amp;)&.{2,6};/g, ' ')

            // for now make the social title the same as the meta title
            return this
        }

        setDescription(description) {
            if (!description) return this

            this.description = description

            return this
        }

        setIsNoIndex() {
            this.isNoIndex = true

            return this.addMetaName('robots', 'noindex,nofollow')
        }

        setCanonical(path) {
            if (!path) return this

            this.canonical = `https://${$sitewideData.domain}${path}`

            return this
        }

        setSocialType(type) {
            if (!type) return this

            this.socialType = type

            return this
        }

        setSocialUrl(path) {
            return this.addMetaProperty('og:url', `https://${$sitewideData.domain}${path}`)
        }

        setSocialImage(url) {
            if (!url) return this
            return this.addMetaProperty('og:image', url)
        }

        addMetaName(name, content) {
            if (!content) return this

            this.metas.push({
                hid: name,
                name,
                content,
            })

            return this
        }

        addMetaProperty(property, content) {
            if (!content) return this

            this.metas.push({
                hid: property,
                property,
                content,
            })

            return this
        }
    }
}
